import { Autocomplete } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
//thunk
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import { autocompleteDropdownStyles } from "Constants/Style";
import { createSelector } from "reselect";
import { fetchCategoryByDropDown } from "slices/Masters/category/thunk";
import { resetSubCategoryOptions } from "slices/Masters/sub-category/reducer";
import { fetchSuCategoryBasedOnSelectedCategory } from "slices/Masters/sub-category/thunk";
import { addType, fetchTypeById, updateType } from "slices/Masters/type/thunk";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

const TypeForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const isEdit = Boolean(id);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [initialData, setInitialData] = useState({
    categoryId: "",
    // categoryName:"",
    subCategoryId: "",
    // subCategoryName: '',
    typeName: "",
  });

  const categoryData = createSelector(
    (state: any) => state.Category,
    (category) => ({
      categoryOptions: category.categoryOptions,
      loading: category.loading,
      error: category.error,
    }),
  );

  const subCategoryData = createSelector(
    (state: any) => state.SubCategory,
    (category) => ({
      subCategoryOptions: category.subCategoryOptions,
      loading: category.loading,
      error: category.error,
    }),
  );

  const {
    categoryOptions,
    loading: catLoading,
    error: catError,
  } = useSelector(categoryData);

  const {
    subCategoryOptions,
    loading: subLoading,
    error: subError,
  } = useSelector(subCategoryData);

  useEffect(() => {
    dispatch(resetSubCategoryOptions());

    return () => {
      dispatch(resetSubCategoryOptions());
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await dispatch(fetchCategoryByDropDown()).unwrap();

        if (id) {
          try {
            const data = await dispatch(fetchTypeById(id)).unwrap();
            console.log("DATA => ", data);
            setInitialData({
              categoryId: data?.category_id,
              // categoryName:data?.category?.category_name,
              subCategoryId: data?.sub_category_id,
              // subCategoryName: data?.sub_category?.sub.category_name,
              typeName: data?.type_name,
            });

            if (data?.category_id) {
              await dispatch(
                fetchSuCategoryBasedOnSelectedCategory(data?.category_id),
              );
            }
          } catch {
            toast.error("Failed to load type");
          } finally {
            setIsLoading(false);
          }
        }
      } catch {
        toast.error("Failed to load dropdown data");
      }
    };

    fetchData();
  }, [dispatch, id, isEdit]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      categoryId: initialData.categoryId,
      // categoryName: initialData.categoryName,
      subCategoryId: initialData.subCategoryId,
      // subCategoryName: initialData.subCategoryName,
      typeName: initialData.typeName,
    },

    validationSchema: Yup.object({
      categoryId: Yup.string().required("Please select a category"),
      subCategoryId: Yup.string().required("Please select a sub-category"),
      typeName: Yup.string().trim().required("Please enter type name"),
    }),
    onSubmit: async (values) => {
      const payload = {
        category_id: values.categoryId,
        sub_category_id: values.subCategoryId,
        type_name: values.typeName.trim(),
      };

      setIsSubmitting(true);
      try {
        if (isEdit && id) {
          await dispatch(updateType({ id, data: payload })).unwrap();
          toast.success("Type updated successfully");
        } else {
          await dispatch(addType(payload)).unwrap();
          clearTableState(TableKeys.MASTER.TYPE);
          toast.success("Type added successfully");
        }
        navigate("/master/type");
      } catch (error: any) {
        console.error("Error during form submit:", error);
        toast.error(error?.message || "Something went wrong while saving type");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit Type" : "Add Type"}
        titleLink="/master/type"
        breadcrumbs={[
          { label: "Type", to: "/master/type" },
          { label: id ? "Edit Type" : "Add Type", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />

      {isEdit && isLoading ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row className="mb-3">
            <Col xs={12} md={4} className="mb-3">
              <Form.Label className="fw-semibold">
                Category <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={categoryOptions}
                getOptionLabel={(option: any) =>
                  typeof option === "string" ? option : option.category_name
                }
                value={
                  categoryOptions.find(
                    (c: any) => c.category_id === formik.values.categoryId,
                  ) || null
                }
                onChange={async (event, selected) => {
                  formik.setFieldValue(
                    "categoryId",
                    selected?.category_id || "",
                  );
                  // Reset sub-category when category changes
                  formik.setFieldValue("subCategoryId", "");
                  // Fetch sub-categories for the selected category
                  if (selected?.category_id) {
                    await dispatch(
                      fetchSuCategoryBasedOnSelectedCategory(
                        selected.category_id,
                      ),
                    );
                  } else {
                    dispatch(resetSubCategoryOptions());
                  }
                }}
                onBlur={() => formik.setFieldTouched("categoryId", true)}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.category_name,
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Category"
                    name="categoryId"
                    error={
                      formik.touched.categoryId &&
                      Boolean(formik.errors.categoryId)
                    }
                    helperText={
                      formik.touched.categoryId && formik.errors.categoryId
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                    }}
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Col>

            <Col xs={12} md={4} className="mb-3">
              <Form.Label className="fw-semibold">
                Sub-category <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={subCategoryOptions}
                getOptionLabel={(option: any) =>
                  typeof option === "string"
                    ? option
                    : option.sub_category_name || option.name
                }
                value={
                  subCategoryOptions.find(
                    (s: any) =>
                      s.sub_category_id === formik.values.subCategoryId ||
                      s.id === formik.values.subCategoryId,
                  ) || null
                }
                loading={subLoading}
                onChange={(event, selected) =>
                  formik.setFieldValue(
                    "subCategoryId",
                    selected?.sub_category_id || selected?.id || "",
                  )
                }
                onBlur={() => formik.setFieldTouched("subCategoryId", true)}
                disabled={!formik.values.categoryId}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.sub_category_name,
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Sub-category"
                    name="subCategoryId"
                    error={
                      formik.touched.subCategoryId &&
                      Boolean(formik.errors.subCategoryId)
                    }
                    helperText={
                      formik.touched.subCategoryId &&
                      formik.errors.subCategoryId
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                    }}
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Col>

            <Col xs={12} md={4} className="mb-3">
              <Form.Label className="fw-semibold">
                Type Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="typeName"
                placeholder="Enter Type name"
                value={formik.values.typeName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.typeName && formik.touched.typeName}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.typeName}
              </Form.Control.Feedback>
            </Col>
          </Row>

          {/* Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton
              redirectTo="/master/type"
              onClick={() => dispatch(resetSubCategoryOptions())}
              disabled={isSubmitting}
            />
            <PrimarySubmitButton
              label={id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={id ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default TypeForm;
